<template>
  <!--
    ProductEditHost
    ----------------
    Mounts the dialog stack used to delete a campaign product or inspect
    its stock:
      - ProductDeleteDialog
      - StockAvailabilityDialog

    All state and template refs live ENTIRELY INSIDE this component.
    Consumers interact via the public API exposed via defineExpose,
    obtained through `<product-edit-host ref="editHost" ... />`. No
    refs cross the component boundary.

    Why this matters (re-attempt 2026-05-08): Vue 3 auto-unwraps refs
    when they appear on a reactive proxy (like `props`). Passing the
    composable in as a prop and reading `:edit.fooRef.value` in a
    template breaks because props.edit.fooRef is already unwrapped on
    arrival; `.value` is undefined, and assignment crashes with
    "Cannot set properties of null (setting 'value')". By keeping all
    refs internal and exposing only computed getters + methods, we
    avoid the problem entirely.

    Usage (Options API consumer):
      <product-edit-host
        ref="editHost"
        :campaign="campaign"
        :products="campaignProducts"
        @campaign-changed="onCampaignChanged"
        @product-upload="onProductUpload"
        @update:modal-popped="onModalPoppedChanged" />

      <product-card
        @delete-product="$refs.editHost.promptDeleteProduct"
        @stockLoaded="$refs.editHost.showStockDialog" />
  -->
  <div class="product-edit-host">
    <product-delete-dialog
      :open="deleteOpen"
      :campaignProduct="deleteProduct"
      :deleting="deletePending"
      @update:open="onDeleteOpenChanged"
      @confirm-delete="onConfirmDelete"
    />

    <stock-availability-dialog
      ref="stockDialogRef"
      :error="stockMessageDisplay"
      :stockInfo="[stockInfoDisplay]"
      :productName="stockProductNameDisplay"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue'
import ProductDeleteDialog from './StoreProducts/ProductDeleteDialog.vue'
import StockAvailabilityDialog from './StoreProducts/StockAvailabilityDialog.vue'
import { useCampaignProductEdit } from '@/composables/useCampaignProductEdit'

export default defineComponent({
  name: 'ProductEditHost',

  components: {
    'product-delete-dialog': ProductDeleteDialog,
    'stock-availability-dialog': StockAvailabilityDialog,
  },

  props: {
    campaign: { type: Object, required: true },
    products: { type: Array, required: true },
  },

  emits: ['campaign-changed', 'product-upload', 'update:modal-popped'],

  setup(props, { emit, expose }) {
    // -------- Template refs (host-internal). --------
    // These point at the actual mounted child component instances.
    const stockDialogRef = ref(null)

    // -------- Reactive mirrors of the props. --------
    // The composable expects refs it can read for `campaign` / `products`.
    // We mirror props into local refs so the composable always reads the
    // latest values without us having to pass props directly (which would
    // be auto-unwrapped on the prop boundary).
    const campaignRef = ref(props.campaign)
    const productsRef = ref(props.products)
    watch(() => props.campaign, (v) => { campaignRef.value = v })
    watch(() => props.products, (v) => { productsRef.value = v })

    // -------- The composable. --------
    // Created HERE, owned HERE. Never passed across a prop boundary.
    const edit = useCampaignProductEdit({
      campaign: campaignRef,
      campaignProducts: productsRef,
      // The composable mutates dialog instances via these getters. They
      // resolve at call-time — no ref-passing.
      getStockDialogInstance: () => stockDialogRef.value,
      onCampaignChanged: (list) => emit('campaign-changed', list),
      onProductUpload: (val) => emit('product-upload', val),
    })

    // -------- Bindings used by the template. --------
    // The template binds plain values, NOT refs. (The template is in the
    // same setup() scope as the refs, so .value access is safe.)
    const deleteOpen = computed(() => edit.deleteProductDialogOpened.value)
    const deleteProduct = computed(() => edit.deleteCampaignProduct.value)
    const deletePending = computed(() => edit.deleteProductPending.value)
    const stockMessageDisplay = computed(() => edit.stockMessage.value)
    const stockInfoDisplay = computed(() => edit.stockInfo.value)
    const stockProductNameDisplay = computed(
      () => edit.selectedStockCampaignProduct.value.product_name
    )

    // -------- modalPopped: permanently false. --------
    // The legacy ProductCreationModal was removed in the 2026-09-01
    // dead-code sweep, so there is no longer anything to "pop". The
    // constant is kept -- and still emitted -- because consumers hide
    // surrounding UI while it is true: StoreProducts/index.vue:315
    // mirrors this event into a watcher that hides the product grid.
    // Reporting a steady `false` keeps the grid visible. Ripping out the
    // emit and its consumer chain would touch the live Products-tab
    // render path for no functional gain; that is a separate follow-up.
    const modalPopped = computed(() => false)

    watch(modalPopped, (val) => {
      emit('update:modal-popped', val)
    })

    // -------- Template event handlers. --------
    // Thin wrappers — no business logic — to keep the template clean
    // and avoid any reactive surprises.
    const onDeleteOpenChanged = (val) => edit.setDeleteDialogOpen(val)
    const onConfirmDelete = (cp) => edit.actuallyDeleteProduct(cp)

    // -------- Public API. --------
    // What consumers call. NO refs leave this object — only:
    //   - methods (functions): safe; refs they close over stay internal.
    //   - getter properties (`get foo()`): return unwrapped .value at
    //     read time. Safe across the boundary because the consumer just
    //     gets the underlying value, not a ref.
    expose({
      // Methods consumers wire to ProductCard events.
      promptDeleteProduct: edit.promptDeleteProduct,
      showStockDialog: edit.showStockDialog,

      // Read-only state, exposed as getters (NOT refs).
      get modalPopped() { return modalPopped.value },
      get deleteDialogOpen() { return edit.deleteProductDialogOpened.value },
    })

    return {
      // template refs
      stockDialogRef,

      // template bindings (computed)
      modalPopped,
      deleteOpen,
      deleteProduct,
      deletePending,
      stockMessageDisplay,
      stockInfoDisplay,
      stockProductNameDisplay,

      // template event handlers
      onDeleteOpenChanged,
      onConfirmDelete,
    }
  },
})
</script>
